import Point from "@arcgis/core/geometry/Point";
import { geographicToWebMercator } from "@arcgis/core/geometry/support/webMercatorUtils";
import MapView from "@arcgis/core/views/MapView";
import {
  WORLD_MAP_FOCUS_ZOOM,
  WORLD_MAP_INITIAL_ZOOM,
  WORLD_MAP_VIEW_CENTER_LAT,
  WORLD_MAP_VIEW_CENTER_LON,
} from "@components/world-map/world-map-constants";
import { WorldMapEntity } from "@components/world-map/world-map-types";
import { getWebMercatorGeometries } from "@components/world-map/world-map-utils";
import { RefObject, useCallback, useEffect, useRef } from "react";
import { watch } from "@arcgis/core/core/reactiveUtils";

interface ReturnProps {
  /** Function to programmatically adjust the map view to fit all entities. */
  viewAllEntities: () => void;
}

/**
 * Tracks which map entities are visible in the current viewport.
 * Fires `onVisibleChange` with all entities whose geographic position
 * falls within the viewport extent whenever the map comes to rest after a pan or zoom,
 * and also updates whenever the entities array changes.
 *
 * Uses refs internally to avoid re-registering the ArcGIS watcher on every render.
 *
 * @param viewRef - Ref to the ArcGIS MapView instance. The watcher is registered once the view is available.
 * @param isViewReady - Whether the ArcGIS view has already been initialized.
 * @param entities - Array of map entities to check against the viewport extent.
 * @param onVisibleChange - Callback invoked with the currently visible entities.
 */
export function useVisibleEntities<T extends WorldMapEntity>(
  viewRef: RefObject<MapView | null>,
  isViewReady: boolean,
  entities: T[],
  onVisibleChange?: (visibleEntities: T[]) => void
): ReturnProps {
  // Keep latest props available inside ArcGIS callbacks (avoid stale closures).
  const entitiesRef = useRef(entities);

  // Sync entity list used by the watcher callback.
  useEffect(() => {
    entitiesRef.current = entities;
  }, [entities]);

  /**
   * Programmatically adjust the map view to fit all entities.
   * If there are no entities, go to the default world view.
   * If there is one entity, set the view to that entity with a focus zoom level.
   * If there are multiple entities, set the view to encompass all entities.
   */
  const viewAllEntities = useCallback(() => {
    const geometries = getWebMercatorGeometries(entitiesRef.current);

    // Prevent animation during the view adjustment. This prevents the potential issue where ArcGIS
    // renders the "moving" animation map on top of the markers, which can cause the markers to disappear.
    const goToOptions: __esri.GoToOptions2D = {
      // eslint-disable-next-line @typescript-eslint/naming-convention -- Name provided by ArcGIS API
      animate: false,
    };

    if (geometries.length === 0) {
      void viewRef.current?.goTo(
        {
          center: [WORLD_MAP_VIEW_CENTER_LON, WORLD_MAP_VIEW_CENTER_LAT],
          zoom: WORLD_MAP_INITIAL_ZOOM,
        },
        goToOptions
      );
      return;
    }

    if (geometries.length === 1) {
      void viewRef.current?.goTo(
        {
          target: geometries[0],
          zoom: WORLD_MAP_FOCUS_ZOOM,
        },
        goToOptions
      );
      return;
    }

    void viewRef.current?.goTo(geometries, goToOptions);
  }, [viewRef]);

  // Extract calculation logic so we can call it on map movement AND when entities update
  const calculateVisibleEntities = useCallback(() => {
    const view = viewRef.current;

    // View must be ready, exist, and we need a callback
    if (!isViewReady || !view || !onVisibleChange) {
      return;
    }

    try {
      const extent = view.extent;
      if (!extent) {
        return;
      }

      // When the user pans the map view sideways is possible that the map view
      // can display 2 extents at the same time. The 2 map extents intersect at
      // international date line. In order to properly calculate the visible entities
      // we use the `normalize` method that returns an array of the currently active
      // extents, either one or two. We can then user this array to check if each entity
      // is contained in at least one of the active extents.
      const normalizedExtents = extent.normalize();

      const visibleEntities: T[] = [];

      entitiesRef.current.forEach((entity) => {
        const geoPoint = new Point({
          longitude: entity.position.long,
          latitude: entity.position.lat,
          spatialReference: { wkid: 4326 },
        });

        const point = geographicToWebMercator(geoPoint);
        const isVisible = normalizedExtents.some((extent) =>
          extent.contains(point as Point)
        );

        if (isVisible) {
          visibleEntities.push(entity);
        }
      });

      onVisibleChange(visibleEntities);
    } catch {
      // Ignore viewport calculation failures
    }
  }, [isViewReady, onVisibleChange, viewRef]);

  // 1. Recalculate whenever the base entities from Redux change
  useEffect(() => {
    calculateVisibleEntities();
  }, [entities, calculateVisibleEntities]);

  // 2. Register the stationary watcher once the view is ready
  useEffect(() => {
    const view = viewRef.current;

    if (!isViewReady || !view) {
      return;
    }

    const handle = watch(
      () => view.stationary,
      (isStationary) => {
        if (isStationary) {
          calculateVisibleEntities();
        }
      }
    );

    return () => handle.remove();
  }, [isViewReady, calculateVisibleEntities, viewRef]);

  return { viewAllEntities };
}
